import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ClientModal from '../components/ClientModal';
import ClientDetailModal from '../components/ClientDetailModal';
import ClientSegmentation from '../components/ClientSegmentation';
import { useClients } from '../hooks/useClients';

const { 
  FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2, FiMail, FiPhone, FiEye, 
  FiUser, FiBuilding, FiGrid, FiList, FiDownload, FiUpload, FiRefreshCw,
  FiUsers, FiArrowUp, FiArrowDown
} = FiIcons;

const Clients = () => {
  const { clients, loading, error, createClient, updateClient, deleteClient, refetch } = useClients();
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filter and search clients
  const filteredClients = clients
    .filter(client => {
      const matchesSearch = 
        client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.segmentation?.account_manager?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'active') return matchesSearch && client.status === 'Active';
      if (filterBy === 'inactive') return matchesSearch && client.status === 'Inactive';
      if (filterBy === 'b2b') return matchesSearch && client.gst_type === 'B2B';
      if (filterBy === 'b2c') return matchesSearch && client.gst_type === 'B2C';
      if (filterBy === 'overdue') return matchesSearch && client.segmentation?.payment_status === 'Overdue';
      if (filterBy === 'pending') return matchesSearch && client.segmentation?.payment_status === 'Pending';
      
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'business':
          aValue = a.business_name || '';
          bValue = b.business_name || '';
          break;
        case 'revenue':
          aValue = a.monthly_revenue || 0;
          bValue = b.monthly_revenue || 0;
          break;
        case 'created':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowDetailModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setShowModal(true);
  };

  const handleDeleteClient = async (id) => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      const result = await deleteClient(id);
      if (result.success) {
        // Success feedback could be added here
      }
    }
  };

  const handleCreateClient = async (clientData) => {
    const result = await createClient(clientData);
    if (result.success) {
      setShowModal(false);
      setSelectedClient(null);
    }
    return result;
  };

  const handleUpdateClient = async (clientData) => {
    if (selectedClient) {
      const result = await updateClient(selectedClient.id, clientData);
      if (result.success) {
        setShowModal(false);
        setSelectedClient(null);
      }
      return result;
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString()}`;
    }
    return amount || '$0';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-200';
      case 'Inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Suspended': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading clients...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Client Management</h1>
          <p className="text-gray-600">
            Comprehensive client profiles with GST compliance and segmentation analytics
          </p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={refetch}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedClient(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Client</span>
          </button>
        </div>
      </motion.div>

      {/* Search, Filter, and Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 min-w-80">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search clients by name, business, email, or manager..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Clients ({clients.length})</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="b2b">B2B</option>
              <option value="b2c">B2C</option>
              <option value="pending">Pending Payments</option>
              <option value="overdue">Overdue</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="name">Sort by Name</option>
              <option value="business">Sort by Business</option>
              <option value="revenue">Sort by Revenue</option>
              <option value="created">Sort by Date Created</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'grid' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'list' ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
          
          {error && (
            <div className="text-red-600 text-sm">
              {error} (Running in demo mode)
            </div>
          )}
        </div>
      </motion.div>

      {/* Client Segmentation Analytics */}
      <ClientSegmentation clients={clients} />

      {/* Clients Display */}
      {filteredClients.length > 0 ? (
        <div className={viewMode === 'grid' ? 
          'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 
          'space-y-4'
        }>
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 border border-gray-200 ${
                viewMode === 'list' ? 'flex items-center justify-between' : ''
              }`}
            >
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{client.name}</h3>
                    <p className="text-gray-600 mb-2">{client.business_name}</p>
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(client.status)}`}>
                        {client.status}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        client.gst_type === 'B2B' ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-purple-100 text-purple-800 border-purple-200'
                      }`}>
                        {client.gst_type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        client.segmentation?.payment_status === 'Active' ? 'bg-green-100 text-green-800 border-green-200' :
                        client.segmentation?.payment_status === 'Pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                        client.segmentation?.payment_status === 'Overdue' ? 'bg-red-100 text-red-800 border-red-200' :
                        'bg-gray-100 text-gray-800 border-gray-200'
                      }`}>
                        {client.segmentation?.payment_status || 'Active'}
                      </span>
                    </div>
                  </div>
                  
                  {viewMode === 'grid' && (
                    <div className="flex items-center space-x-1 ml-2">
                      <button 
                        onClick={() => handleViewClient(client)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                      </button>
                      <button 
                        onClick={() => handleEditClient(client)}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Edit Client"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                      </button>
                      <button 
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Client"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{client.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{client.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{client.segmentation?.account_manager || 'Not assigned'}</span>
                  </div>
                  {client.gstin && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiBuilding} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600 font-mono">GSTIN: {client.gstin}</span>
                    </div>
                  )}
                </div>

                {viewMode === 'grid' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-gray-800">{client.projects || 0}</p>
                        <p className="text-xs text-gray-500">Projects</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-800">{formatCurrency(client.monthly_revenue)}</p>
                        <p className="text-xs text-gray-500">Monthly</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-800">{formatCurrency(client.total_billed)}</p>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {viewMode === 'list' && (
                <div className="flex items-center space-x-4 ml-6">
                  <div className="text-right min-w-24">
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(client.monthly_revenue)}</p>
                    <p className="text-xs text-gray-500">Monthly Revenue</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button 
                      onClick={() => handleViewClient(client)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="View Details"
                    >
                      <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleEditClient(client)}
                      className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                      title="Edit Client"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                    </button>
                    <button 
                      onClick={() => handleDeleteClient(client.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete Client"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-12 shadow-soft text-center border border-gray-200"
        >
          <SafeIcon icon={FiUsers} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {searchTerm || filterBy !== 'all' ? 'No clients found' : 'No clients yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Start by adding your first client to begin managing your business relationships'
            }
          </p>
          {(!searchTerm && filterBy === 'all') && (
            <button
              onClick={() => {
                setSelectedClient(null);
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              Add Your First Client
            </button>
          )}
        </motion.div>
      )}

      {/* Modals */}
      <ClientModal 
        isOpen={showModal} 
        onClose={() => {
          setShowModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onSubmit={selectedClient ? handleUpdateClient : handleCreateClient}
      />

      <ClientDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onEdit={handleEditClient}
        onDelete={handleDeleteClient}
      />
    </div>
  );
};

export default Clients;